import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { INDUSTRIES, INDUSTRY_SLUGS } from "../lib/industriesData";
import { SITE_URL } from "../lib/seo";

export default function IndustriesPage() {
    useSEO({
        title: "Industries We Serve | UK Hospitality Consultancy — Optimum Hospitality Solutions",
        description:
            "Hospitality consulting across UK independent hotels, boutique hotels, branded hotels, aparthotels, restaurants and food and beverage, and hospitality groups. Sector specific operating support, led by Sidney Adams.",
        path: "/industries",
    });

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE_URL}/industries` },
        ],
    };
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: INDUSTRY_SLUGS.map((slug, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/industries/${slug}`,
            name: INDUSTRIES[slug].title,
        })),
    };

    return (
        <div data-testid="industries-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Industries</span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        Industries we serve.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">
                        We work across six sectors of UK hospitality. Each one has a distinct operating model and a distinct set of margin and guest experience levers. Engagements are scoped against the sector you operate in, the property in front of us and the brief.
                    </p>
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border">
                        {INDUSTRY_SLUGS.map((slug, i) => {
                            const s = INDUSTRIES[slug];
                            return (
                                <Link
                                    key={slug}
                                    to={`/industries/${slug}`}
                                    data-testid={`industry-card-${slug}`}
                                    className="bg-brand-stone p-8 lg:p-10 hover:bg-white transition-colors flex flex-col"
                                >
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h2 className="font-display text-xl lg:text-2xl mt-4 text-brand-navy leading-snug">{s.title}</h2>
                                    <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed flex-1">
                                        {s.intro.length > 200 ? `${s.intro.slice(0, 200).trim()}…` : s.intro}
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide self-start">
                                        Learn more <ArrowUpRight size={14} />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Ready to improve operational performance, profitability and guest experience?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Schedule a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to="/booking" className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
